import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

/**
 * Hook to track referral clicks across all pages
 * Automatically detects ?ref= parameter and tracks it
 */
export const useReferralTracking = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get('ref');
    
    if (!refCode) {
      return;
    }

    // Check if already tracked recently (prevent double tracking)
    const lastTracked = localStorage.getItem('aimetapic_ref_last_tracked');
    const lastTrackedCode = localStorage.getItem('aimetapic_ref') || getCookie('aimetapic_ref');
    
    // If same code and tracked within last 1 hour, skip
    if (lastTracked && lastTrackedCode === refCode) {
      const hourAgo = Date.now() - (60 * 60 * 1000);
      if (parseInt(lastTracked) > hourAgo) {
        console.log('Referral already tracked recently, skipping');
        return;
      }
    }

    trackReferralClick(refCode);
  }, [searchParams]);

  // Helper function to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  };

  const trackReferralClick = async (refCode: string) => {
    try {
      console.log('🎯 Tracking referral click:', refCode);
      
      // Store in BOTH localStorage AND cookie for better persistence
      const expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000);
      
      // Store in localStorage (fallback for same browser)
      localStorage.setItem('aimetapic_ref', refCode);
      localStorage.setItem('aimetapic_ref_expires', expiresAt.toString());
      localStorage.setItem('aimetapic_ref_last_tracked', Date.now().toString());
      
      // Store in cookie (works across tabs, more persistent)
      const cookieExpires = new Date(expiresAt).toUTCString();
      document.cookie = `aimetapic_ref=${refCode}; expires=${cookieExpires}; path=/; SameSite=Lax`;
      document.cookie = `aimetapic_ref_expires=${expiresAt}; expires=${cookieExpires}; path=/; SameSite=Lax`;

      // Call backend tracking API
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/referral/click?ref=${refCode}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Referral click tracked successfully:', data);
        toast.success('Referral code applied! 🎉', { duration: 3000 });
      } else {
        console.warn('⚠️ Failed to track referral click:', data);
        // Don't show error to user - silent fail
      }
    } catch (error) {
      console.error('❌ Error tracking referral click:', error);
      // Silent fail - don't interrupt user experience
    }
  };
};
